import { Component, OnInit, OnDestroy } from '@angular/core';
import { UsersService } from '@app/services/users/users.service'; 
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from '@angular/material/dialog';
import { ImageCropperComponent } from '@app/shared/components/image-cropper/image-cropper.component';
import { ImageUploadService } from '@app/services/image-upload/image-upload.service';
import { map, Observable, skipWhile, startWith, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';
import * as CoreActions from '@app/core/store/core.action'
import { selectImageProfleImageState } from '@app/modules/image/store/selectors/image.selectors';
import { SubSink } from 'subsink';
import { selectMeState } from '@app/core/store/core.selectors';
import { IUser } from '@app/models/user.interface';
import { CustomValidators } from '@app/modules/users/validators/custom-validators';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { lang_selected$ } from '@app/shared/components/language-selector/language-selector.component';
import { ChangeProfilePasswordComponent } from './change-profile-password/change-profile-password.component';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {
  public currentUser$:IUser = null;
  public subs: SubSink = new SubSink();
  public profileLogo$
  public form: FormGroup;
  public profileUrl$: Subscription;
  public imageUrl$ = '';
  public avatarUrl$: string = '';
  public isHidden = true;
  public isSubmitted = false;
  public isProd = true;
  private stop$;
  public historySubscription: any;
  public tierID = 0;
  cardCollapsed = true;
  public text_tier_1 = "";
  public text_tier_2 = "";
  public text_tier_3 = "";
  public text_tier_4 = "";
  height_box = '';
  lang_selected: string;
  height_plan_title = ''; 
  public subscription: Subscription;
  OGAdminID = '';
  OGAdminFullName = '';
  actualUsrID = '';
  isOGAdmin = 0;
  public showPricing = false;
  public timezones$: any;
  public allTimezones: any[] = [];
  public timeZoneSearchControl = new FormControl();

  public upgradeText = 'Enhance your Tier for better features!';
  public upgradeUrl = 'https://www.trakrsuite.com/trakr-counting-pricing';

  public manageText = 'Manage your subscription level effortlessly!';
  public manageUrl = 'https://trakrsuite.chargebeeportal.com';

  public companyName = 'trakrSuite';
  public columns: any[] = [];



  toggleCardCollapse() {
    this.cardCollapsed = !this.cardCollapsed;
  }

  getColumnDefs() {
    return this.columns.map(column => column.field);
  }

  constructor(
    private _users: UsersService,
    private _fb: FormBuilder,
    private _matDialog: MatDialog,
    private _imageUpload: ImageUploadService,
    private store$: Store,
    private _translation: TranslateService,
    private _toaster: ToastrService,
    public route: ActivatedRoute
  ) {
    const baseUrl: string = `${window.location.hostname}`;
    if(baseUrl!="counting.trakrsuite.com"){
      this.isProd = false;
      this.manageUrl = 'https://trakrsuite-test.chargebeeportal.com';
    }

    this.columns = [
      { field: 'tier_name', header: '' }, 
      { field: 'date_registered', header: '' } 
    ];

    this.store$.select(selectMeState).pipe(
      skipWhile(res => res == null),
      take(2)
    ).subscribe((me) => {
      this.OGAdminID = me.userOG[0].usrID;
      this.OGAdminFullName = me.userOG[0].usrFirstName +' '+me.userOG[0].usrLastName;
      this.actualUsrID = me.usrID;
      if(this.OGAdminID != this.actualUsrID){
        this.isOGAdmin=1;
      }
      
      this.columns[0].header = this._translation.instant('subscriptionType');
      this.columns[1].header = this._translation.instant('date');
      this.historySubscription = me.tierHistory;
      this.tierID = me.tier_id;
      if (this.tierID === 1) {
        this.text_tier_1 = this._translation.instant('yourTier');
        this.text_tier_2 = this._translation.instant('upgrade');
        this.text_tier_3 = this._translation.instant('upgrade');
        this.text_tier_4 = this._translation.instant('contactUs');
      } else if (this.tierID === 2) {
        this.text_tier_1 = this._translation.instant('downgrade');
        this.text_tier_2 = this._translation.instant('yourTier');
        this.text_tier_3 = this._translation.instant('upgrade');
        this.text_tier_4 = this._translation.instant('contactUs');
      } else if (this.tierID === 3) {
        this.text_tier_1 = this._translation.instant('downgrade');
        this.text_tier_2 = this._translation.instant('downgrade');
        this.text_tier_3 = this._translation.instant('yourTier');
        this.text_tier_4 = this._translation.instant('contactUs');
      } else if (this.tierID === 4) {
        this.text_tier_1 = this._translation.instant('downgrade');
        this.text_tier_2 = this._translation.instant('downgrade');
        this.text_tier_3 = this._translation.instant('downgrade');
        this.text_tier_4 = this._translation.instant('yourTier');
      }
    });


    this.store$.dispatch(CoreActions.setTitle({ title: this.route.snapshot.data['title'] }));
    this.form = this._fb.group({
      usrUsername: new FormControl(''),
      usrFirstName: new FormControl(''),
      usrLastName: new FormControl(''),
      usrPhone: new FormControl(''),
      usrTimeZone: new FormControl(''),
      usrEmail: new FormControl(''),
      usrPassword: new FormControl('', Validators.compose([
        // check whether the entered password has a number
        CustomValidators.patternValidator(/\d/, {
          hasNumber: true
        }),
        // check whether the entered password has upper case letter
        CustomValidators.patternValidator(/[A-Z]/, {
          hasCapitalCase: true
        }),
        // check whether the entered password has a lower case letter
        CustomValidators.patternValidator(/[a-z]/, {
          hasSmallCase: true
        }),
        // check whether the entered password has a special character
        CustomValidators.patternValidator(
          /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
          {
            hasSpecialCharacters: true
          }
        ),
        Validators.minLength(8)
      ])),
      usrIsPrivileged: new FormControl(0)
    });



   }

   private _filter(value: string): string[] {
      const filterValue = value.toLowerCase().replace(/ /g, '_');
      return this.allTimezones.filter(option => option.toLowerCase().includes(filterValue));
    }

   ngAfterViewInit() {
    let scroll = localStorage.getItem('scroll');
    if(scroll!='yes'){
      const scrollToElement = document.getElementById('upgrade');
      if (scrollToElement) {
        scrollToElement.scrollIntoView();
      }
    }else{
      localStorage.removeItem('scroll');
    }
  }

  ngOnInit(): void {

    this.subscription = lang_selected$.subscribe(value => {
      this.lang_selected = value;
      if(this.lang_selected != 'en-ca'){
        this.height_box = 'height_box';
        this.height_plan_title = 'height_plan_title'; 
      }else{
        this.height_box = '';
        this.height_plan_title = '';
      }
    });

    this.store$.dispatch(CoreActions.loadMe());

   this.subs.add(this.store$.select(selectMeState).pipe(
      skipWhile(res => res == null),
    ).subscribe((res) =>  {
      this.currentUser$ = res;
      this.timeZoneSearchControl.setValue(res.usrTimeZone);
      this.avatarUrl$ = `https://ui-avatars.com/api/?size=128&name=${res.usrFirstName}+${res.usrLastName}`
      this.form = this._fb.group({
        usrUsername: new FormControl(res.usrUsername, [Validators.required]),
        usrFirstName: new FormControl(res.usrFirstName, [Validators.required]),
        usrLastName: new FormControl(res.usrLastName, [Validators.required]),
        usrPhone: new FormControl(res.usrPhone),
        usrTimeZone: new FormControl(res.usrTimeZone, [Validators.required]),
        usrEmail: new FormControl(res.usrEmail, [Validators.required]),
        usrPassword: new FormControl('', Validators.compose([
          // check whether the entered password has a number
          CustomValidators.patternValidator(/\d/, {
            hasNumber: true
          }),
          // check whether the entered password has upper case letter
          CustomValidators.patternValidator(/[A-Z]/, {
            hasCapitalCase: true
          }),
          // check whether the entered password has a lower case letter
          CustomValidators.patternValidator(/[a-z]/, {
            hasSmallCase: true
          }),
          // check whether the entered password has a special character
          CustomValidators.patternValidator(
            /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
            {
              hasSpecialCharacters: true
            }
          ),
          Validators.minLength(8)
        ])),
        usrIsPrivileged: new FormControl(res.usrIsPrivileged)
      });

      this.form.controls['usrTimeZone'].valueChanges
      .subscribe(value => {
        this.timezones$ = this._filter(value || '');
      });

    }));

    this.subs.add(this.store$.select(selectImageProfleImageState).subscribe((imageUrl) => this.imageUrl$ = imageUrl));



    //get timezone from api
    this._users.onGetTimezone().subscribe((res) => {
      this.timezones$ = res;
      this.allTimezones = res;
    });

    // this.timezones$ = this.form.get('usrTimeZone').valueChanges.pipe(
    //   startWith(''),
    //   map(value => this._filter(value || '')),
    // );

  }

  // private _filter(value: string): string[] {
  //   console.log(value);
  //   const filterValue = value.toLowerCase();

  //   return this.allTimezones.filter(option => option.toLowerCase().includes(filterValue));
  // }

openChargebeePlan(plan){

  //window.open(this.manageUrl, "_blank");
  
  if(this.isOGAdmin === 1 ){
    return false;
  }

  if(plan==1){
    if(this.tierID==1){
      return false;
    }
    if(this.isProd){
      window.open("https://trakrsuite.chargebeeportal.com", "_blank");
      //window.open("https://trakrsuite.chargebee.com/hosted_pages/checkout?subscription_items%5Bitem_price_id%5D%5B0%5D=trakr-Counting-FREE", "_blank");
    }else{
      window.open("https://trakrsuite-test.chargebeeportal.com", "_blank");
      //window.open("https://trakrsuite-test.chargebee.com/hosted_pages/checkout?subscription_items[item_price_id][0]=trakr-Counting-FREE-CAD-Monthly&subscription_items[quantity][0]=1&layout=in_app", "_blank");
    }
  }else if(plan==2){
    if(this.tierID==2){
      return false;
    }
    if(this.isProd){
      window.open("https://trakrsuite.chargebeeportal.com", "_blank");
      //window.open("https://trakrsuite.chargebee.com/hosted_pages/checkout?subscription_items[item_price_id][0]=Counting-Tier-1-CAD-Monthly&utm_source=cb-app-copy", "_blank");
    }else{
      window.open("https://trakrsuite-test.chargebeeportal.com", "_blank");
      //window.open("https://trakrsuite-test.chargebee.com/hosted_pages/checkout?subscription_items[item_price_id][0]=trakr-Counting-TIER-1-CAD-Monthly&subscription_items[quantity][0]=1&layout=in_app", "_blank");
    }
  }else if(plan==3){
    if(this.tierID==3){
      return false;
    }
    if(this.isProd){
      window.open("https://trakrsuite.chargebeeportal.com", "_blank");
      //window.open("https://trakrsuite.chargebee.com/hosted_pages/checkout?subscription_items[item_price_id][0]=Counting-Tier-2-Monthly-CAD-Monthly&utm_source=cb-app-copy", "_blank");
    }else{
      window.open("https://trakrsuite-test.chargebeeportal.com", "_blank");
      //window.open("https://trakrsuite-test.chargebee.com/hosted_pages/checkout?subscription_items[item_price_id][0]=trakr-Counting-TIER-2-CAD-Monthly&subscription_items[quantity][0]=1&layout=in_app", "_blank");
    }
  }else if(plan==4){
    if(this.tierID==4){
      return false;
    }
    window.open("https://share.hsforms.com/1dNtOh44vT9a9mVwHpZmZowcpfw0", "_blank");
  }
  
}

ngOnDestroy(){
  this.subs.unsubscribe();
}

onSubmit(){
  this.timeZoneSearchControl.setValue(this.form.value.usrTimeZone);
  this.store$.dispatch(CoreActions.updateMe({ form: this.form.value }));
  this._toaster.success(this._translation.instant('user.profile.updated'));
}

onOpenUpload(){
  return this._matDialog.open(ImageCropperComponent, {
    data:{
      type: 'profile'
    },
    width:'30vw'
  });
}

openChangePassword(): void {
  const dialogRef = this._matDialog.open(ChangeProfilePasswordComponent, {
    width: '500px', 
  });

  dialogRef.afterClosed().subscribe(result => {
    //console.log('Modal closed', result);
  });
}

onTimezoneSelection(timezone: any) {
  this.timeZoneSearchControl.setValue(timezone);
  this.form.get('usrTimeZone').setValue(timezone);
}

}
